import { saveAs } from 'file-saver';
import { notification } from 'antd';
import { useCallback } from 'react';
import {UseLazyQuery, UseMutation} from "@reduxjs/toolkit/dist/query/react/buildHooks";
import {BaseQueryFn, MutationDefinition, QueryDefinition} from "@reduxjs/toolkit/query";
import {FetchArgs, FetchBaseQueryError} from "@reduxjs/toolkit/query/react";
import { TagType } from '../../../store/services/api/base-api';
import {ReportResponse} from "../../../store/services/api/report-api";
import {convertBase64toBlob} from "../../../utils/helpers";

interface UseReportProps<TParams> {
  query: UseLazyQuery<QueryDefinition<TParams, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, TagType, ReportResponse, "api">> |
    UseMutation<MutationDefinition<TParams, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, TagType, ReportResponse, "api">>;
}

export const useReport = <TParams,>({query}: UseReportProps<TParams>) => {
  const [getReport, status] = query();

  const handleError = useCallback(() => {
    notification.error({
      message: 'Error',
      description: 'There was an issue downloading the report.',
      placement: 'bottomRight',
      duration: 3,
    });
  }, []);

  const handleNotice = useCallback((noticeMessage: string) => {
    notification.warning({
      message: 'Export adjusted',
      description: noticeMessage,
      placement: 'bottomRight',
      duration: 5,
    });
  }, []);

  const downloadReport = useCallback(
    async (params: TParams, contentType: string = 'application/pdf') => {
      try {
        const data = await getReport(params).unwrap();
        if (!data) {
          handleError();
          return;
        }

        const blob = data.reportData instanceof Blob
          ? data.reportData
          : convertBase64toBlob(data.reportData, data.contentType ?? contentType);

        saveAs(blob, data.fileName);
        if (data.notice) {
          handleNotice(data.notice);
        }
      } catch {
        handleError();
      }
    },
    [query, handleError, handleNotice, getReport]
  );

  return {
    downloadReport,
    isLoading: status?.isLoading || status?.isFetching,
  };
};
